import React, { useEffect } from "react";
import { useNavigationGuard } from "../../../hooks/useNavigationGuard";

/**
 * Navigation Guard Component
 * Wraps the main application content and handles browser navigation
 */
const NavigationGuard = ({
  children,
  onNavigateBack = null,
  preventBrowserBack = true,
  showExitConfirmation = true,
}) => {
  // Custom back handler for SPA navigation
  const handleSPABack = (event) => {
    console.log("NavigationGuard: Handling SPA back navigation");

    // Check if there's a custom handler
    if (onNavigateBack) {
      onNavigateBack(event);
      return;
    }

    // Default SPA behavior - stay in app but show user-friendly message
    if (showExitConfirmation) {
      const shouldExit = window.confirm(
        "Going back will take you out of KinShield. Are you sure you want to leave?"
      );

      if (shouldExit) {
        // User wants to leave - allow it
        window.removeEventListener("popstate", handleSPABack);
        window.history.go(-2); // Go back twice to actually leave
      } else {
        // User wants to stay - push state again to prevent leaving
        window.history.pushState(null, "", window.location.pathname);
      }
    }
  };

  // Initialize navigation guard
  const { navigateWithinApp, replaceCurrentState, safeExit } =
    useNavigationGuard({
      onNavigateBack: handleSPABack,
      preventBrowserBack,
      confirmMessage: showExitConfirmation
        ? "Are you sure you want to leave KinShield? Any unsaved changes will be lost."
        : null,
    });

  // Provide navigation methods to child components via context if needed
  useEffect(() => {
    // Make navigation utilities available globally if needed
    window.KinShieldNavigation = {
      navigateWithinApp,
      replaceCurrentState,
      safeExit,
    };

    return () => {
      // Cleanup
      delete window.KinShieldNavigation;
    };
  }, [navigateWithinApp, replaceCurrentState, safeExit]);

  return <>{children}</>;
};

export default NavigationGuard;
